export type AssignationCancellationCategory = "Customer" | "Driver" | "Support";

type AssignationCancellationReasonCustomer =
    | "Connection issue (flight, train, ship etc...)"
    | "Booked somewhere else"
    | "No-show"
    | "Upset with Daytrip about another trip"
    | "Upset with Daytrip about the payment issue"
    | "Medical"
    | "Other";

type AssignationCancellationReasonDriver =
    | "Unavailable"
    | "Bad price"
    | "Illness"
    | "Personal"
    | "Mechanical issue"
    | "No-show"
    | "Late pick up"
    | "Other";

type AssignationCancellationReasonSupport = "Mistake" | "Changing trips/assignations" | "Other";

export type ManualAssignationCancellationReason =
    | AssignationCancellationReasonCustomer
    | AssignationCancellationReasonDriver
    | AssignationCancellationReasonSupport;

export const CANCELLATION_CATEGORIES = ["Customer", "Driver", "Support"] as const;
export const ASSIGNATION_CANCELLATION_CATEGORIES =
    CANCELLATION_CATEGORIES as readonly AssignationCancellationCategory[];
export const ASSIGNATION_CANCELLATION_REASONS: {
    [Key in AssignationCancellationCategory]: ManualAssignationCancellationReason[];
} = {
    Customer: [
        "Connection issue (flight, train, ship etc...)",
        "Booked somewhere else",
        "No-show",
        "Upset with Daytrip about another trip",
        "Upset with Daytrip about the payment issue",
        "Medical",
        "Other",
    ],
    Driver: ["Unavailable", "Bad price", "Illness", "Personal", "Mechanical issue", "No-show", "Late pick up", "Other"],
    Support: ["Mistake", "Changing trips/assignations", "Other"],
};

type AutomatedAssignationCancellationReason =
    | "This vehicle category was removed"
    | "Vehicle category has been changed"
    | "Trip was cancelled"
    | "Driver was assigned to the company"
    | "Order was assigned to different ride"
    | "Mismatch between orders and assignations"
    | "No accepted transportation offer"
    | "Transportation offer changed"
    | "Ride assignation cancelled"
    | "Trips can no longer be performed as a Combo"
    | "Trip reassigned to a personal combo";

type AssignationCancellationReason = ManualAssignationCancellationReason | AutomatedAssignationCancellationReason;

export type CancellationInfo = {
    category: AssignationCancellationCategory | "Automated cancellation";
    reason: AssignationCancellationReason;
    note?: string;
};

const createAutomatedCancellationInfo = (reason: AutomatedAssignationCancellationReason): CancellationInfo => ({
    category: "Automated cancellation",
    reason,
});

export const VEHICLE_REMOVED_CANCELLATION_INFO = createAutomatedCancellationInfo("This vehicle category was removed");
export const VEHICLE_CHANGED_CANCELLATION_INFO = createAutomatedCancellationInfo("Vehicle category has been changed");
export const TRIP_CANCELLATION_INFO = createAutomatedCancellationInfo("Trip was cancelled");
export const DRIVER_REASSIGNED_CANCELLATION_INFO = createAutomatedCancellationInfo(
    "Driver was assigned to the company",
);

// Ride cancellations
export const DIFFERENT_RIDE_CANCELLATION_INFO = createAutomatedCancellationInfo("Order was assigned to different ride");
export const MISMATCH_BETWEEN_ORDERS_AND_ASSIGNATIONS_CANCELLATION_INFO = createAutomatedCancellationInfo(
    "Mismatch between orders and assignations",
);
export const NO_ACCEPTED_TRANSPORTATION_OFFER_CANCELLATION_INFO = createAutomatedCancellationInfo(
    "No accepted transportation offer",
);
export const TRANSPORTATION_OFFER_CHANGED_CANCELLATION_INFO =
    createAutomatedCancellationInfo("Transportation offer changed");
export const RIDE_ASSIGNATION_CANCELLED_CANCELLATION_INFO =
    createAutomatedCancellationInfo("Ride assignation cancelled");
export const COMBO_BROKEN_CANCELLATION_INFO = createAutomatedCancellationInfo(
    "Trips can no longer be performed as a Combo",
);
export const PERSONAL_COMBO_CANCELLATION_INFO = createAutomatedCancellationInfo("Trip reassigned to a personal combo");

export const cancellationInfoToString = (info?: CancellationInfo, reason?: string): string => {
    if (info) {
        return `${info.category}: ${info.reason}${info.note ? `, ${info.note}` : ""}`;
    }
    return reason ?? "";
};
